import { useMemo, useState, useEffect } from 'react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useTranslation } from 'react-i18next'

interface ResultsChartProps {
  data: Record<string, unknown>[]
  type?: 'line' | 'bar' | 'pie' | 'auto'
  xKey?: string
  yKeys?: string[]
  title?: string
  description?: string
  /** When present (e.g. stats results), used for axis choice and formatting */
  metadata?: { statsColumnTypes?: Record<string, string> }
  resultType?: 'table' | 'stats' | 'signals'
}

const formatColumnLabel = (key: string) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

export default function ResultsChart({
  data,
  type = 'auto',
  xKey,
  yKeys,
  title,
  description,
  metadata,
  resultType,
}: ResultsChartProps) {
  const { t } = useTranslation()
  const statsTypes = metadata?.statsColumnTypes ?? {}
  const hasStatsMetadata = resultType === 'stats' && Object.keys(statsTypes).length > 0

  // Auto-detect keys if not provided
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return []
    if (Array.isArray(data) && data.length > 0) {
      return data.slice(0, 100) // Limit to 100 points for performance
    }
    return []
  }, [data])

  const groupColumns = useMemo(
    () => Object.entries(statsTypes).filter(([, v]) => v === 'group').map(([k]) => k),
    [statsTypes]
  )
  const useCompositeX = hasStatsMetadata && groupColumns.length > 1
  const COMPOSITE_X_KEY = '__xLabel__'

  const chartDisplayData = useMemo(() => {
    if (!chartData.length) return []
    if (useCompositeX) {
      return chartData.map((row) => ({
        ...row,
        [COMPOSITE_X_KEY]: groupColumns.map((col) => row[col]).join(' | '),
      }))
    }
    return chartData
  }, [chartData, useCompositeX, groupColumns])

  const detectedXKey = useMemo(() => {
    if (xKey) return xKey
    if (useCompositeX) return COMPOSITE_X_KEY
    if (hasStatsMetadata && chartData.length > 0) {
      const firstGroup = groupColumns.find((k) => chartData[0] && k in chartData[0])
      if (firstGroup) return firstGroup
      return Object.keys(chartData[0]).find((k) => statsTypes[k] !== 'group') ?? Object.keys(chartData[0])[0]
    }
    const commonKeys = ['time', 'timestamp', 'date', 'datetime', 't', 'x']
    for (const key of commonKeys) {
      if (chartData[0] && key in chartData[0]) return key
    }
    if (chartData.length > 0) return Object.keys(chartData[0])[0]
    return 'index'
  }, [chartData, xKey, hasStatsMetadata, statsTypes, useCompositeX, groupColumns])

  const detectedYKeys = useMemo(() => {
    if (yKeys && yKeys.length > 0) return yKeys
    if (chartData.length === 0) return []
    const keys = Object.keys(chartData[0])
    if (hasStatsMetadata) {
      const numericTypes = ['count', 'percent', 'avg', 'max', 'min', 'stddev', 'median']
      return keys.filter((key) => {
        if (key === detectedXKey) return false
        const colType = statsTypes[key]
        if (colType === 'group') return false
        if (colType && numericTypes.includes(colType)) return true
        return typeof chartData[0][key] === 'number'
      })
    }
    return keys.filter((key) => {
      if (key === detectedXKey) return false
      return typeof chartData[0][key] === 'number'
    })
  }, [chartData, yKeys, detectedXKey, hasStatsMetadata, statsTypes])

  // Percent columns: pie makes sense; when multiple, one chart + selector to avoid many cards
  const percentKeys = useMemo(
    () =>
      detectedYKeys.filter(
        (k) => statsTypes[k] === 'percent' || /pct|percent|accuracy|rate/i.test(k)
      ),
    [detectedYKeys, statsTypes]
  )
  const [selectedPercentKey, setSelectedPercentKey] = useState<string>(percentKeys[0] ?? '')
  const [pieGroupBy, setPieGroupBy] = useState<string>('')
  useEffect(() => {
    if (percentKeys.length > 0 && !percentKeys.includes(selectedPercentKey)) {
      setSelectedPercentKey(percentKeys[0])
    }
  }, [percentKeys, selectedPercentKey])
  const effectivePieGroupBy =
    groupColumns.length > 0
      ? (groupColumns.includes(pieGroupBy)
          ? pieGroupBy
          : useCompositeX
            ? COMPOSITE_X_KEY
            : groupColumns[0])
      : detectedXKey

  const getColumnChartType = (key: string): 'pie' | 'bar' | 'line' => {
    if (type !== 'auto') return type as 'pie' | 'bar' | 'line'
    const colType = statsTypes[key]
    if (colType === 'percent') return 'pie'
    if (colType === 'count' || colType === 'max' || colType === 'min') return 'bar'
    if (colType === 'avg' || colType === 'stddev' || colType === 'median') return 'line'
    const lowerKey = key.toLowerCase()
    if (lowerKey.includes('percent') || /pct|accuracy|rate/i.test(key)) return 'pie'
    if (lowerKey.includes('count')) return 'bar'
    if (chartData.length > 0 && detectedXKey) {
      const xVal = chartData[0][detectedXKey]
      if (typeof xVal !== 'number' && typeof xVal !== 'object') return 'bar'
    }
    return 'line'
  }

  const formatValueByType = (key: string, value: number): string => {
    if (statsTypes[key] === 'percent') return `${value.toFixed(1)}%`
    if (statsTypes[key] === 'count' || statsTypes[key] === 'max' || statsTypes[key] === 'min') return String(Math.round(value))
    return value.toLocaleString(undefined, { maximumFractionDigits: 1 })
  }

  // Group columns: one card for all percent (pie + selector if multiple), one card per other metric
  const chartGroups = useMemo(() => {
    if (type !== 'auto' && detectedYKeys.length > 0) {
      return [{ type: type as 'pie' | 'bar' | 'line', keys: detectedYKeys }]
    }
    const groups: Array<{ type: 'pie' | 'bar' | 'line'; keys: string[] }> = []
    if (percentKeys.length > 0) {
      groups.push({ type: 'pie', keys: percentKeys })
    }
    detectedYKeys
      .filter((key) => !percentKeys.includes(key))
      .forEach((key) => {
        groups.push({ type: getColumnChartType(key), keys: [key] })
      })
    if (groups.length === 0 && detectedYKeys.length > 0) {
      const defaultType = chartData.length > 0 && detectedXKey && typeof chartData[0][detectedXKey] !== 'number' ? 'bar' : 'line'
      groups.push({ type: defaultType, keys: detectedYKeys })
    }
    return groups
  }, [type, detectedYKeys, detectedXKey, chartData, statsTypes, percentKeys])

  if (!chartData || chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          {title && <CardTitle>{title}</CardTitle>}
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            {t('results.noDataAvailable')}
          </p>
        </CardContent>
      </Card>
    )
  }

  // Color palette for charts
  const COLORS = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', 
    '#82CA9D', '#FFC658', '#FF7C7C', '#8DD1E1', '#D084D0'
  ]

  // Render single chart component (dataOverride = data for pie; pieValueKey = key for value when aggregated)
  const renderChart = (
    chartType: 'pie' | 'bar' | 'line',
    keys: string[],
    dataOverride?: Record<string, unknown>[],
    pieValueKey?: string
  ) => {
    const dataForChart = dataOverride ?? chartDisplayData
    if (chartType === 'pie') {
      const pieDataKey = pieValueKey ?? keys[0]
      const pieData = dataForChart.map((item: Record<string, unknown>) => ({
        name: String(item[detectedXKey] ?? item[COMPOSITE_X_KEY] ?? ''),
        value: Number(item[pieDataKey] ?? 0),
      }))
      const isPercent = statsTypes[pieDataKey] === 'percent'
      return (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={(props: { name?: string; percent?: number }) =>
                `${props.name ?? ''}: ${((props.percent ?? 0) * 100).toFixed(0)}%`
              }
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number | undefined) =>
                isPercent ? `${(value ?? 0).toFixed(1)}%` : String(Math.round(value ?? 0))
              }
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      )
    }

    const ChartComponent = chartType === 'bar' ? BarChart : LineChart
    const DataComponent = chartType === 'bar' ? Bar : Line
    const tooltipFormatter = (value: number | undefined, name: string | undefined) => {
      const key = keys.find((k) => formatColumnLabel(k) === (name ?? '')) ?? keys[0]
      return [value != null ? formatValueByType(key, value) : '—', formatColumnLabel(key)]
    }

    return (
      <ResponsiveContainer width="100%" height={400}>
        <ChartComponent data={chartDisplayData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={detectedXKey}
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={80}
            tickFormatter={(v) => String(v)}
          />
          <YAxis tick={{ fontSize: 12 }} tickFormatter={keys.length === 1 ? (v) => formatValueByType(keys[0], Number(v)) : undefined} />
          <Tooltip formatter={(value: unknown, name: string | undefined) => tooltipFormatter(Number(value), name)} />
          <Legend formatter={(name) => name} />
          {keys.map((key, index) => (
            <DataComponent
              key={key}
              dataKey={key}
              stroke={COLORS[index % COLORS.length]}
              fill={COLORS[index % COLORS.length]}
              name={formatColumnLabel(key)}
            />
          ))}
        </ChartComponent>
      </ResponsiveContainer>
    )
  }

  return (
    <div className="space-y-6">
      {chartGroups.map((group, groupIndex) => {
        const isPercentCard = group.type === 'pie'
        const isPercentMulti = isPercentCard && group.keys.length > 1
        const effectivePieKey =
          isPercentMulti && selectedPercentKey && group.keys.includes(selectedPercentKey)
            ? selectedPercentKey
            : group.keys[0]
        const primaryKey = group.keys[0]
        const groupTitle = isPercentMulti
          ? 'Percent metrics'
          : formatColumnLabel(primaryKey)

        const pieDataForCard =
          isPercentCard && groupColumns.length > 0 && effectivePieGroupBy !== COMPOSITE_X_KEY
            ? (() => {
                const byKey: Record<string, { sum: number; count: number }> = {}
                chartDisplayData.forEach((row) => {
                  const key = String(row[effectivePieGroupBy] ?? '')
                  const val = Number(row[effectivePieKey] ?? 0)
                  if (!byKey[key]) byKey[key] = { sum: 0, count: 0 }
                  byKey[key].sum += val
                  byKey[key].count += 1
                })
                return Object.entries(byKey).map(([name, { sum, count }]) => ({
                  [detectedXKey]: name,
                  [COMPOSITE_X_KEY]: name,
                  __pieValue: count ? sum / count : 0,
                })) as Record<string, unknown>[]
              })()
            : null

        return (
          <Card key={groupIndex}>
            <CardHeader>
              {groupIndex === 0 && title && (
                <CardTitle className="text-xl mb-2">{title}</CardTitle>
              )}
              {groupIndex === 0 && description && (
                <CardDescription className="mb-2">{description}</CardDescription>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-lg">{groupTitle}</CardTitle>
                {(isPercentCard && groupColumns.length > 0) || isPercentMulti ? (
                  <div className="flex flex-wrap items-center gap-3">
                    {isPercentMulti && (
                      <div className="flex items-center gap-1.5">
                        <Label className="text-xs text-muted-foreground whitespace-nowrap">Metric</Label>
                        <Select
                          value={selectedPercentKey || group.keys[0]}
                          onValueChange={setSelectedPercentKey}
                        >
                          <SelectTrigger className="h-8 w-[180px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {group.keys.map((key) => (
                              <SelectItem key={key} value={key} className="text-xs">
                                {formatColumnLabel(key)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {isPercentCard && groupColumns.length > 0 && (
                      <div className="flex items-center gap-1.5">
                        <Label className="text-xs text-muted-foreground whitespace-nowrap">Group by</Label>
                        <Select
                          value={effectivePieGroupBy}
                          onValueChange={setPieGroupBy}
                        >
                          <SelectTrigger className="h-8 w-[140px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {groupColumns.map((col) => (
                              <SelectItem key={col} value={col} className="text-xs">
                                {formatColumnLabel(col)}
                              </SelectItem>
                            ))}
                            {useCompositeX && (
                              <SelectItem value={COMPOSITE_X_KEY} className="text-xs">
                                {groupColumns.map((c) => formatColumnLabel(c)).join(' · ')}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {(() => {
                const dataToUse = isPercentCard && pieDataForCard && pieDataForCard.length > 0 ? pieDataForCard : chartDisplayData
                const usePieAggregate = isPercentCard && pieDataForCard && pieDataForCard.length > 0
                return dataToUse.length > 0 ? (
                  renderChart(
                    group.type,
                    isPercentMulti ? [effectivePieKey] : group.keys,
                    isPercentCard ? dataToUse : undefined,
                    usePieAggregate ? '__pieValue' : undefined
                  )
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No data to show.
                  </p>
                )
              })()}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
